import {
  BrowserMatcher,
  DeviceMatcher,
  DomainMatcher,
  InstantConfigInterpreter,
  InstantConfigLoader,
  InstantConfigOverrider,
  InstantConfigValue,
  RegionMatcher,
} from "@wikia/instant-config-loader";
import { inject, injectWithTransform, singleton } from "tsyringe";
import {
  Logger,
  LoggerTransformer,
  Device,
  QueryString,
  Platform,
  Browser,
  Geo,
} from "@identity/utils";
import { InstantConfigCacheService } from "./instant-config-cache-service";
import { COOKIES, WINDOW } from "@identity/symbols";
import { CommunicationService } from "../CommunicationService";

@singleton()
export class InstantConfigService {
  private repository: Dictionary<InstantConfigValue>;

  constructor(
    @inject(Device) private readonly device: Device,
    @inject(Browser) private readonly browser: Browser,
    @inject(Platform) private readonly platform: Platform,
    @inject(Geo) private readonly geo: Geo,
    @inject(WINDOW) private readonly window: Window,
    @inject(COOKIES) private readonly cookiesManager: CookiesManager,
    @injectWithTransform(Logger, LoggerTransformer, "InstantConfig")
    private readonly logger: Logger,
  ) {}

  async init(
    communicationService: CommunicationService,
    globals: Dictionary = {},
  ): Promise<InstantConfigService> {
    const appName = this.platform.get();
    const geo = this.geo.get();
    const instantConfigLoader = new InstantConfigLoader({ appName });
    const instantConfigInterpreter = new InstantConfigInterpreter(
      new BrowserMatcher(this.browser.getBrowser()),
      new DeviceMatcher(this.device.getDeviceType()),
      new DomainMatcher(),
      new RegionMatcher(geo),
      new InstantConfigCacheService(
        this.window,
        this.cookiesManager,
        communicationService,
      ),
    );

    let config = await instantConfigLoader.getConfig();
    config = new InstantConfigOverrider().override(
      QueryString.getURLSearchParams(),
      config,
    );
    const interpreter = instantConfigInterpreter.init(config, globals);
    this.repository = interpreter.getValues();

    this.logger.info("instantiated with", this.repository);

    return this;
  }

  get<T extends InstantConfigValue>(key: string, defaultValue?: T): T {
    if (key in this.repository && this.repository[key] !== undefined) {
      return this.repository[key] as any;
    }

    return defaultValue;
  }
}
